import { useEffect, useRef, useState } from 'react';

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useInView(ref, { once = true, rootMargin = '0px 0px -12% 0px', threshold = 0.12 } = {}) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting && once) io.disconnect();
      },
      { rootMargin, threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, once, rootMargin, threshold]);

  return inView;
}

// Cycles through `count` items every `ms` while `running`. Selecting an item
// manually restarts the timer from that item.
export function useAutoCycle(count, ms, running) {
  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!running) return undefined;
    const id = setTimeout(() => setIndex((i) => (i + 1) % count), ms);
    return () => clearTimeout(id);
  }, [index, cycle, count, ms, running]);

  const select = (i) => {
    setIndex(i);
    setCycle((c) => c + 1);
  };

  return [index, select, cycle];
}

// 0 → 1 as the element travels through the viewport (top enters bottom edge
// at `start`, bottom leaves at `end`, both in viewport-height fractions).
export function useScrollProgress(ref, { start = 0.85, end = 0.35 } = {}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const from = vh * start;
      const to = vh * end - r.height;
      setProgress(Math.min(1, Math.max(0, (from - r.top) / (from - to))));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ref, start, end]);

  return progress;
}

export function useElementWidth(ref) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width;
}

export function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
